import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDependency } from '../../../contexts/DependencyProvider';
import { useViewInfo } from '../../../hooks/UseViewInfo';
import { statusColor } from '../../../utils/helperUtil';
import useBrowserName from '../../../hooks/UseBrowserName';
import useScreenSize from '../../../hooks/UseScreenSize';
import moment from 'moment';
import { useAsyncList } from 'react-stately';
import {
  Flex,
  View,
  ProgressCircle,
  Grid,
  TableView,
  TableBody,
  TableHeader,
  Row,
  Cell,
  Column,
  Heading,
  Content,
  ListBox,
  Item,
  IllustratedMessage,
  Link,
  DialogContainer,
} from '@adobe/react-spectrum';
import { CircleFill } from 'react-bootstrap-icons';
import NotFound from '@spectrum-icons/illustrations/NotFound';
import { FinderIcon } from '../../../components/protected/Icons/IconsLib';
import FilterComponent from './component/FilterComponent';
import HeaderComponent from './component/HeaderComponent';
import { invokeFinderApp } from '../../../utils/NavigationUtils';
import { ListActivityAdvancedRequest } from '../../../services/soap/project/requests/ListActivityAdvancedRequest';
import { ListStatusRequest, ListStatusType } from '../../../services/soap/project/requests/ListStatusRequest';
import { ListFormSettingsRequest } from '../../../services/soap/features/requests/ListFormSettingsRequest';
import { LayoutDefinition } from '../../../infra/protected/Layout/LayoutDefinitions';
import { GetFormPanelRequest } from '../../../services/soap/features/requests/GetFormPanelRequest';
import { SoapUtils } from '../../../utils/SoapUtils';
import { GetFormSettingsRequest } from '../../../services/soap/features/requests/GetFormSettingsRequest';
import { ListRegisterRequest } from '../../../services/soap/features/requests/ListRegisterRequest';
import CardComponent from './component/CardComponent';
import { ComponentFactory } from '../../../components/protected/layout-components/ComponentFactory';
import FilterSearchTextBox from './component/FilterSearchTextBox';
import { FPAData, FPADataFactory } from '../../../infra/protected/FPA/FPAData';
import { usePreloadAssets } from '../../../hooks/UsePreloadAssets';
import { RootState } from '../../../state/store';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { updateAppInfo } from '../../../state/userWebLayout/userWebLayoutSlice';
import { ContextNewDialog } from '../../../components/protected/Context/ContextNewDialog';
import { DataProvider } from '../../../components/protected/data-components/Contexts/DataContext';
import styles from './ServiceActivities.module.css';
import { addKey, useKeyboardShortcuts } from '../../../hooks/UseKeyboardShortcuts';

export interface Character {
  id: string;
  name: string;
}

export function ServiceActivities() {
  const { activityStatusMapping } = usePreloadAssets();
  const browserName = useBrowserName();
  const screenSize = useScreenSize();
  const { t } = useTranslation();
  const { isMobile } = useViewInfo();
  const { store, projectService, featureService } = useDependency();
  const dataStore = useSelector((state: any) => state.userWebLayout.appInfos);
  const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedAppId);
  const dispatch = useDispatch();
  const { state } = useLocation();
  const [showLoader, setShowLoader] = useState(true);
  const [showLoaderDetail, setShowLoaderDetail] = useState(true);
  const [rowsData, setRowsData] = useState<any>([]);
  const [columnsData, setColumnsData] = useState<any>([]);
  const [pageView, setPageView] = useState<string>('Full');
  const [selectedKeys, setSelectedKeys] = useState<Set<any>>();
  const [activityStatusList, setActivityStatusList] = useState<any>([]);
  const [definition, setDefinition] = useState<LayoutDefinition | null>(null);
  const [showMobileDetailView, setShowMobileDetailView] = useState<boolean>(false);
  const [stopLoadData, setStopLoadData] = useState<boolean>(false);
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<FPAData | null>(null);
  const [selectedDataRow, setSelectedDataRow] = useState<any>({});
  const serviceActivityValue = useRef<string>('');
  const queryParams = useRef<any>({ FILTER: { COL: [] } });
  const totalActivityListRec = useRef<number>(0);
  const offSetLimit = useRef<number>(0);
  const dataRef = useRef<any>({});
  const [showAdd, setShowAdd] = useState(false);
  const PAGE_SIZE = 50;

  useEffect(() => {
    if (state.appKey && dataStore[state.appKey]) {
      if (dataStore[state.appKey].pageView) {
        dataRef.current.pageView = dataStore[state.appKey].pageView;
        setPageView(dataStore[state.appKey].pageView);
        return;
      }
    }
  }, [dataStore.appInfos, state.appKey]);

  useKeyboardShortcuts([
    ...addKey('c', { ctrl: true, shift: true } , () => handleAddNew()),
  ],true);
  useEffect(() => {
    (async () => {
      setShowLoader(true);
      setShowLoaderDetail(true);
      queryParams.current = { FILTER: { COL: [] } };
      serviceActivityValue.current = '';
      setPageView('Full');
      setSearchedValue('');
      Promise.all([getActivityStatusArray()]);
    })();
  }, [selectedApp]);

  useEffect(() => {
    if (selectedApp !== '') {
      listActivityList.reload();
    }
  }, [selectedApp]);

  useEffect(() => {
    return () => {
      dispatch(
        updateAppInfo({
          key: state.appKey,
          data: {
            pageView: dataRef.current.pageView,
          },
        })
      );
    };
  }, []);

  const getFormSettings = useCallback(
    async (settingId: string) => {
      //console.log('settingId', settingId);
      if (settingId === '') {
        const resFormSettings = await featureService.listFormSettings(new ListFormSettingsRequest(store.Server, store.SessionId));
        let filteredData = resFormSettings.SETTING.filter((item: any) => item.formId === 'ListActivityAdvanced' && item.isDefault == '1');
        if (filteredData.length > 0) {
          settingId = filteredData[0].id.toString();
        }
      }
      const response = await featureService.getFormSettings(new GetFormSettingsRequest(store.Server, store.SessionId, settingId));
      if (response?.result === 'OK' && response?.count > 0) {
        const filteredData = response.SETTING[0]?.ITEM?.filter(item => String(item.visibility) === '1');
        const result = filteredData?.map(item => {
          const key = item.itemname.includes('|') ? item.itemname.split('|')[1] : item.itemname;
          const name = item.caption && item.caption.trim() !== '' ? item.caption : item.name;
          return { key, name };
        });
        setColumnsData(result);
      }
    },
    [selectedApp]
  );

  const getActivityStatusArray = useCallback(async () => {
    const statusArray: any = [];
    let res: any = await projectService.listActivityStatus2(new ListStatusRequest(store.Server, store.SessionId, ListStatusType.Activity, serviceActivityValue.current));

    if (res.result === 'OK' && res.STATUS.length > 0) {
      res?.STATUS.forEach((status: any) => {
        // Extract id and name from each STATUS object
        const { id, name, color } = status;
        let colorString = statusColor(color);
        // Push an object containing id and name to the statusArray
        statusArray.push({ id, name, color: colorString });
      });

      statusArray.unshift({ id: 'All states', name: 'All states', color: 'black', types: [] });
      setActivityStatusList(statusArray);
    }
  }, []);

  const getAcitivityListKey = useCallback(async () => {
    let resListRegister = await featureService.listRegister(new ListRegisterRequest(store.Server, store.SessionId, undefined, undefined, undefined, undefined, +selectedApp));
    //console.log('resListRegister', resListRegister);

    serviceActivityValue.current = resListRegister?.ROWS?.find((row: any) => row.ROW.key === 'FPATypeCategoryForListActivity')?.ROW.value ?? '';

    let settingId = resListRegister?.ROWS?.find((row: any) => row.ROW.key === 'formSetting')?.ROW.value ?? '';

    await getFormSettings(settingId);

    return resListRegister?.ROWS?.find((row: any) => row.ROW.key === 'FPATypeCategoryForListActivity')?.ROW.value ?? '';
  }, [selectedApp]);

  const getAcitivityListCount = async () => {
    const fpaTypeCategoryExists = queryParams.current.FILTER.COL.some((col: any) => col.name === 'fpaTypeCategory');
    if (!fpaTypeCategoryExists) {
      queryParams.current.FILTER.COL.push({
        name: 'fpaTypeCategory',
        type: 'any',
        value: serviceActivityValue.current !== '' ? serviceActivityValue.current : await getAcitivityListKey(),
      });
    }

    // Remove duplicates in the FILTER.COL array
    const uniqueCOL: any = [];
    const typeName = new Set();

    queryParams.current.FILTER.COL.forEach((item: any) => {
      if (!typeName.has(item.name)) {
        typeName.add(item.name);
        uniqueCOL.push(item);
      }
    });

    // Update the COL array with unique values
    queryParams.current.FILTER.COL = uniqueCOL;

    let activityRes = await projectService.listActivityAdvanced(
      new ListActivityAdvancedRequest(store.Server, store.SessionId, {
        countOnly: '1',
        FILTER: queryParams.current.FILTER,
      })
    );
    if (activityRes?.result == 'OK') {
      totalActivityListRec.current = activityRes.count != 0 ? activityRes.count : 0;
      return activityRes.count != 0 ? activityRes.count : 0;
    }
  };

  const loadServiceActivities = async () => {
    try {
      if (stopLoadData) {
        return rowsData;
      }
      setSearchedValue('');
      let activityRes = await projectService.listActivityAdvanced(
        new ListActivityAdvancedRequest(store.Server, store.SessionId, {
          limit: PAGE_SIZE,
          offset: offSetLimit.current ?? 0,
          FILTER: queryParams.current.FILTER,
          ORDER: {
            COL: [
              {
                name: 'created',
                desc: 'true',
              },
            ],
          },
          FIELDS: {
            field: columnsData.map((column: any) => column.key),
          },
        })
      );

      if (activityRes?.result == 'OK' && activityRes?.count > 0) {
        // add fullPath to each activity
        let listActivity: any = activityRes?.ACTIVITIES?.ACTIVITY;

        setRowsData(listActivity);
        if (listActivity.length > 0) {
          setSelectedKeys(new Set([listActivity[0].id]));
          setSelectedDataRow(listActivity[0]);
          getLayout(listActivity[0]);
        }
        return listActivity;
      } else {
        setRowsData([]);
        //ToastQueue.info(t('no_results', { ns: 'layout_components' }), { timeout: 50 });
        return [];
      }
    } finally {
      setShowLoader(false);
    }
  };

  let listActivityList = useAsyncList<Character>({
    async load({ cursor }) {
      //console.log('cursor', cursor);
      if (selectedApp === '') {
        setShowLoader(false);
        return { items: rowsData, cursor: undefined };
      }
      if (cursor && Number(cursor) * PAGE_SIZE >= totalActivityListRec.current) {
        setShowLoader(false);
        return { items: rowsData, cursor: undefined };
      }
      let totalRecords = await getAcitivityListCount();
      totalRecords = totalRecords ? Number(totalRecords) : 0;
      //console.log('totalRecords', totalRecords);
      let currentPage = cursor ? parseInt(cursor) : 0;
      //console.log('currentPage', currentPage);
      let offSet = PAGE_SIZE * currentPage;
      //console.log('offset', offSet);
      offSetLimit.current = offSet;
      let itemList = await loadServiceActivities();
      //console.log('itemList', itemList);
      return {
        items: itemList,
        cursor: String(offSet >= totalRecords ? undefined : currentPage + 1),
      };
    },
  });

  const reloadServiceActivities = useCallback((filterParams: any) => {
    setStopLoadData(false);
    queryParams.current = filterParams;
    totalActivityListRec.current = 0;
    offSetLimit.current = 0;
    listActivityList.reload();
  }, []);

  const addStatusColor = (statusName: any) => {
    const statusObj = activityStatusList.find((obj: any) => obj.name === statusName);
    return statusObj?.color;
  };

  const handleTableRowClick = (rowId: any): void => {
    invokeFinderApp(rowId);
  };

  const handleListRowClick = (rowId: any): void => {
    if (selectedKeys?.has(rowId.currentKey)) {
      return;
      setSelectedDataRow({});
      setSelectedKeys(undefined);
      setDefinition(null);
    } else {
      setShowLoaderDetail(true);
      setSelectedKeys(rowId);
      setPageView('Full');
      dataRef.current.pageView = 'Full';
      let selectedRow = rowsData.find((item: any) => item.id === rowId.currentKey);
      if (!selectedRow) {
        setSelectedDataRow({});
        setSelectedKeys(undefined);
        setDefinition(null);
        return;
      }
      setSelectedDataRow(selectedRow);
      getLayout(selectedRow);
    }
  };

  const handleRowClickMobile = (rowId: any): void => {
    const nameSearchExists = queryParams.current.FILTER.COL.find((col: any) => col.name === 'nameSearch');
    if (nameSearchExists) {
      setSearchedValue(nameSearchExists.value);
    }
    setSelectedKeys(undefined);
    let selectedRow = rowsData.find((item: any) => item.id === rowId.currentKey);
    if (!selectedRow) {
      setSelectedDataRow({});
      setSelectedKeys(undefined);
      setDefinition(null);
      return;
    }
    setSelectedDataRow(selectedRow);
    getLayout(selectedRow);
    setShowMobileDetailView(true);
  };

  const handleViewChange = (view: string) => {
    setPageView(view);
    dataRef.current.pageView = view;
  };

  const getStatusColor = (statusName: any) => {
    const statusObj = activityStatusList.find((obj: any) => obj.name === statusName);
    return (
      <>
        <CircleFill style={{ paddingRight: '10px' }} size={'18px'} color={statusObj?.color} /> {statusName}
      </>
    );
  };

  const getLayout = async (selectedRow: any) => {
    try {
      let resSelectedItem = FPADataFactory.createFromActivityAdvanced(selectedRow, activityStatusMapping);
      setSelectedItem(resSelectedItem);
      let typeId = selectedRow.type;
      const layout = await featureService.getFormPanel(new GetFormPanelRequest(store.Server, store.SessionId, '' + typeId));

      if (layout.count > 0) {
        let scheme = SoapUtils.parseXmlString2(layout.ROW.SCHEME);
        let layoutObject = JSON.parse(scheme.ActivityPanelSettings.layoutObject);
        setDefinition(layoutObject);
      } else {
        setDefinition(null);
      }
    } catch (err) {
      console.error('getlayout error: ', err);
    } finally {
      setShowLoaderDetail(false);
    }
  };

  const Loader = () => (
    <Flex width="100%" justifyContent="center" marginTop={10}>
      <ProgressCircle aria-label="Loading…" isIndeterminate />
    </Flex>
  );

  const NoResultsMessage = () => (
    <Flex direction="row" width="100%" alignContent="center" justifyContent="center">
      <IllustratedMessage>
        <NotFound />
        <Heading>{t('no_results', { ns: 'layout_components' })}</Heading>
        <Content>{t('try_another_search', { ns: 'layout_components' })}</Content>
      </IllustratedMessage>
    </Flex>
  );

  const getHeight = (baseHeight: number) => {
    if (browserName === 'Chrome') {
      return `${baseHeight - 120}px`;
    } else if (browserName === 'Safari') {
      return `${baseHeight - 120}px`;
    } else {
      return `${baseHeight - 120}px`;
    }
  };

  const getWidth = () => {
    // if (browserName === 'Chrome') {
    //   return `95%`;
    // } else if (browserName === 'Safari') {
    //   return `95%`;
    // } else {
    //   return `95%`;
    // }
    return `95%`;
  };

  const detailStyle = {
    height: getHeight(screenSize.height - 20),
  };

  const cardStyle = {
    height: getHeight(screenSize.height),
    width: getWidth(),
  };

  const filterStyle = {
    height: getHeight(screenSize.height + 60),
    overflow: 'hidden auto',
  };

  const handleAddNew = () => {
    setShowAdd(true);
  };

  const handleAddNewClose = (reload_flag: boolean = false) => {
    if (reload_flag) listActivityList.reload();
    setShowAdd(false);
  };
  const handleAddNewCloseDismiss = () => {
    setShowAdd(false);
    listActivityList.reload();
  };
  return (
    <>
      {isMobile || window.innerWidth < 1030 ? (
        <>
          {!showMobileDetailView && (
            <>
              <FilterComponent onFilterChange={reloadServiceActivities} filterParams={queryParams.current} />
              <View position={'relative'} width={'100%'} height={'100%'} UNSAFE_className={styles.card_list_parent}>
                <Flex direction={'column'} position={'absolute'} width={'100%'} top={'72px'} left={'0%'} alignItems={'start'} justifyContent={'start'} gap={'12px'}>
                  {showLoader ? (
                    <Loader />
                  ) : (
                    <ListBox
                      id="listActivityCardsMobile"
                      selectionMode="single"
                      onSelectionChange={handleRowClickMobile}
                      marginStart={'18px'}
                      maxHeight={'100vh'}
                      minHeight={'130px'}
                      defaultSelectedKeys={selectedKeys}
                      aria-label="Service Activities Cards Mobile"
                      items={listActivityList.items}
                      isLoading={listActivityList.isLoading}
                      width={'90%'}
                    >
                      {item => (
                        <Item key={item.id} textValue={item.name}>
                          <CardComponent item={item} activityStatusList={activityStatusList} />
                        </Item>
                      )}
                    </ListBox>
                  )}
                </Flex>
                <Flex
                  direction={'column'}
                  position={'absolute'}
                  width={'calc(100% - 32px)'}
                  top={'16px'}
                  right={'16px'}
                  left={'16px'}
                  alignItems={'start'}
                  justifyContent={'start'}
                  UNSAFE_className={styles.search_parent}
                >
                  <Flex direction={'row'} alignSelf={'stretch'} height={'40px'} flexShrink={0} alignItems={'center'} justifyContent={'end'} gap={'8px'} UNSAFE_className={styles.search_field}>
                    <FilterSearchTextBox onSearchChange={reloadServiceActivities} filterParams={queryParams.current} serachedText={searchedValue} fpaTypeCategory={serviceActivityValue.current} />
                    <i className={`${styles.icon_search} bi bi-search`}></i>
                  </Flex>
                </Flex>
                <Flex
                  direction={'row'}
                  position={'fixed'}
                  right={'16px'}
                  bottom={'95px'}
                  height={'48px'}
                  justifyContent={'center'}
                  alignItems={'center'}
                  gap={'8px'}
                  UNSAFE_className={styles.add_new_parent}
                >
                  <Link
                    isQuiet
                    onPress={e => {
                      handleAddNew();
                    }}
                    UNSAFE_className={styles.add_new_btn}
                  >
                    <i className="bi bi-plus fs-5">
                      <View UNSAFE_className={styles.add_new_btn_text}>{t('add_new', { ns: 'layout_components' })}</View>
                    </i>
                  </Link>
                </Flex>
              </View>
            </>
          )}

          {showMobileDetailView && (
            <>
              <View position={'relative'} width={'100%'} height={'64px'} overflow={'hidden'} UNSAFE_className={styles.layout_header_parent}>
                <View position={'absolute'} top={'calc(50% - 14px)'} left={'15%'} UNSAFE_className={styles.layout_header_text}>
                  {selectedItem?.item_type_name}
                </View>
                <View position={'absolute'} top={'calc(50% - 15px)'} left={'25px'} width={'24px'} height={'30px'} overflow={'hidden'} UNSAFE_className={styles.layout_header_btn_back}>
                  <Link
                    onPress={() => {
                      setStopLoadData(true);
                      setShowMobileDetailView(false);
                    }}
                  >
                    <i className="bi bi-chevron-left fs-5" />
                  </Link>
                </View>
                <a
                  onClick={() => {
                    setStopLoadData(true);
                    setShowMobileDetailView(false);
                  }}
                  className={styles.layout_header_btn_folder}
                >
                  <FinderIcon size={24} />
                </a>
              </View>
              <Flex direction="column" alignItems={'center'} justifyContent={'center'} UNSAFE_className={styles.detailsHolder} UNSAFE_style={{ padding: '20px', paddingTop: '0px' }}>
                {selectedItem && (
                  <>
                    {showLoader ? (
                      <Loader />
                    ) : (
                      <>
                        <View
                          UNSAFE_className={styles.service_status_bar}
                          marginBottom={'5px'}
                          marginTop={'0px'}
                          UNSAFE_style={{ backgroundColor: addStatusColor(selectedDataRow.customStateName) }}
                        ></View>
                        <ComponentFactory
                          selectedItem={selectedItem}
                          definition={definition}
                          systemAttributes={{
                            onPrint: item => console.log('onPrint', item),
                            onEdit: item => console.log('onEdit', item),
                            onDelete: item => console.log('onDelete', item),
                            onChat: item => console.log('onChat', item),
                            onConfig: (item, type) => console.log('onConfig', item, type),
                          }}
                        />
                      </>
                    )}
                  </>
                )}
              </Flex>
            </>
          )}
        </>
      ) : (
        <Grid
          areas={pageView == 'Full' ? ['filter header header', 'filter card detail'] : ['filter header header', 'filter detail detail']}
          columns={{ base: ['245px', '28%', '55%'], L: ['245px', '28%', '55%'] }}
          rows={['auto', '1fr']}
          height="100%"
          width="100%"
          UNSAFE_style={{ backgroundColor: '#f5f6f9', padding: '0px', margin: '0px', overflow: 'hidden' }}
        >
          <View gridArea="filter">
            <FilterComponent onFilterChange={reloadServiceActivities} filterParams={queryParams.current} filterStyle={filterStyle} />
          </View>
          <View width={'100%'} gridArea="header" UNSAFE_style={pageView == 'Full' ? {} : { height: '100%' }}>
            <HeaderComponent
              onPageViewChange={handleViewChange}
              autoChanged={pageView}
              filterParams={queryParams.current}
              onSearchChange={reloadServiceActivities}
              fpaTypeCategory={serviceActivityValue.current}
              onAddNew={handleAddNew}
            />
          </View>
          {pageView == 'Full' ? (
            <>
              {showLoader ? (
                <Loader />
              ) : (
                <>
                  <View gridArea="card" UNSAFE_style={{ backgroundColor: '#f5f6f9' }}>
                    {rowsData?.length > 0 ? (
                      <ListBox
                        autoFocus
                        id="listActivityCards"
                        selectionMode="single"
                        onSelectionChange={handleListRowClick}
                        marginStart={'15px'}
                        minHeight={'130px'}
                        defaultSelectedKeys={selectedKeys}
                        aria-label="Service Activities Cards"
                        items={listActivityList.items}
                        isLoading={listActivityList.isLoading}
                        UNSAFE_style={cardStyle}
                        marginTop={'13px'}
                      >
                        {item => (
                          <Item key={item.id} textValue={item.name}>
                            <CardComponent item={item} activityStatusList={activityStatusList} />
                          </Item>
                        )}
                      </ListBox>
                    ) : (
                      <View marginTop={'13px'}>
                        <NoResultsMessage />
                      </View>
                    )}
                  </View>
                  <View
                    gridArea="detail"
                    backgroundColor={'static-white'}
                    borderRadius={'large'}
                    padding={'size-100'}
                    paddingTop={'0'}
                    marginTop={'17px'}
                    UNSAFE_style={detailStyle}
                    width={'98%'}
                    UNSAFE_className={styles.detailView}
                  >
                    {definition &&
                      (showLoaderDetail ? (
                        <Loader />
                      ) : (
                        <View height="100%" UNSAFE_className={styles.detailsHolder} UNSAFE_style={{ overflow: 'hidden auto' }} marginStart={'10px'}>
                          {selectedItem && (
                            <>
                              {rowsData?.length > 0 ? (
                                <>
                                  <View UNSAFE_className={styles.service_status_bar} UNSAFE_style={{ backgroundColor: addStatusColor(selectedDataRow.customStateName) }}></View>
                                  <View height={'2px'}>&nbsp;</View>
                                  <ComponentFactory
                                    selectedItem={selectedItem}
                                    definition={definition}
                                    systemAttributes={{
                                      onPrint: item => console.log('onPrint', item),
                                      onEdit: item => console.log('onEdit', item),
                                      onDelete: item => console.log('onDelete', item),
                                      onChat: item => console.log('onChat', item),
                                      onConfig: (item, type) => console.log('onConfig', item, type),
                                    }}
                                  />
                                </>
                              ) : (
                                ''
                              )}
                            </>
                          )}
                        </View>
                      ))}
                  </View>
                </>
              )}
            </>
          ) : (
            <View gridArea="detail">
              {showLoader ? (
                <Loader />
              ) : (
                <View position={'relative'} overflow={'auto'} height={'100%'} width={'100%'} UNSAFE_className={styles.service_activities}>
                  {columnsData?.length > 0 && rowsData?.length > 0 ? (
                    <TableView
                      UNSAFE_className="tblLayoutComponent"
                      aria-label="Service Activities"
                      // onSelectionChange={handleTableRowClick}
                      selectionMode="single"
                      defaultSelectedKeys={selectedKeys}
                      selectionStyle="highlight"
                      width={{ base: '100%', L: '100%', M: '1100px' }}
                      minHeight={'100px'}
                      maxHeight={{ base: '100vh', L: '625px' }}
                      onAction={handleTableRowClick}
                    >
                      <TableHeader columns={columnsData}>
                        {(column: any) => (
                          <Column showDivider key={column.key}>
                            {column.name}
                          </Column>
                        )}
                      </TableHeader>
                      <TableBody items={listActivityList.items} loadingState={listActivityList.loadingState}>
                        {(item: any) => (
                          <Row>
                            {columnKey => (
                              <Cell>
                                {(() => {
                                  if (moment(item[columnKey], moment.ISO_8601, true).isValid()) {
                                    return moment(item[columnKey]).format('MM/DD/YYYY | hh:mm A');
                                  } else if (columnKey === 'customStateName') {
                                    return getStatusColor(item[columnKey]);
                                  } else {
                                    return item[columnKey];
                                  }
                                })()}
                              </Cell>
                            )}
                          </Row>
                        )}
                      </TableBody>
                    </TableView>
                  ) : (
                    <NoResultsMessage />
                  )}
                </View>
              )}
            </View>
          )}
        </Grid>
      )}
      <DataProvider>
        <DialogContainer onDismiss={handleAddNewCloseDismiss}>
          {showAdd && <ContextNewDialog parentItem={null} onClose={handleAddNewClose} additionalMode={true} additionalData={{ fpaTypeCategory: serviceActivityValue.current, fpaType: 'ACTIVITY' }} />}
        </DialogContainer>
      </DataProvider>
    </>
  );
}
